import { ChevronDown, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  formatCount,
  formatCurrency,
  formatMinutes,
  formatPercent,
  formatPrice,
} from '@/lib/format'
import type { UtilizationLine } from '../types'
import { RowLabel } from './RowLabel'
import { COST_COLUMN_DIVIDER, COUNT_COLUMN } from './utilizationClasses'

// Stable per-row DOM id, built from the row's whole path. A description
// alone would collide — every duty has a "Calls" group — so toggling one
// would toggle them all and aria-controls would point at the wrong rows.
export const rowDomId = (path: string) =>
  `utilization-row-${path}`.toLowerCase().replace(/[^a-z0-9-]+/g, '-')

// A chevron (size-3.5) and the gap-1.5 after it, which is what a group's
// label is offset by. A row without a chevron takes the same offset so the
// labels line up.
const CHEVRON_GUTTER = 'inline-block pl-5'
// What an add-on shows for Count: it is a percentage of the bill, not
// something counted.
const NO_UNITS = '--'

/** Indentation and weight per nesting level. */
const DEPTH_STYLES = [
  { cell: 'py-3', row: 'border-b border-dashed border-border', text: '' },
  {
    cell: 'py-2 pl-6',
    row: 'border-b border-dashed border-border text-muted-foreground',
    text: '',
  },
  {
    cell: 'py-1.5 pl-12',
    row: 'border-b border-dashed border-border/60 text-muted-foreground',
    text: 'text-xs',
  },
]

interface UtilizationRowProps {
  line: UtilizationLine
  depth: number
  /** The row's whole path, which its DOM id is built from. */
  path: string
  isCollapsed: boolean
  /** DOM ids of the rows directly beneath it, for aria-controls. */
  childIds: string
  onToggle: () => void
}

/** One line of the utilization table: its label (a toggle when it has
 *  sub-rows), Count, Minutes, Price and Amount. */
export function UtilizationRow({
  line,
  depth,
  path,
  isCollapsed,
  childIds,
  onToggle,
}: UtilizationRowProps) {
  const style = DEPTH_STYLES[Math.min(depth, DEPTH_STYLES.length - 1)]!
  const hasChildren = (line.subRows ?? []).length > 0
  const isAddon = line.usageFraction != null
  // The subtotal restates the money above it. Count, Minutes and Price
  // belong to the lines it sums, and repeating them here would read as
  // another charge.
  const moneyOnly = isAddon || line.isSubtotal
  return (
    <tr id={rowDomId(path)} className={style.row}>
      <td
        className={`${style.cell} ${style.text} ${line.isSubtotal ? 'font-semibold' : ''}`}>
        {hasChildren ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggle}
            aria-expanded={!isCollapsed}
            // Collapsed groups must not reference rows that aren't
            // rendered.
            aria-controls={isCollapsed ? undefined : childIds}
            className={`h-auto p-0 has-[>svg]:px-0 font-normal hover:bg-transparent ${style.text}`}>
            {isCollapsed ? (
              <ChevronRight className="size-3.5 text-muted-foreground" />
            ) : (
              <ChevronDown className="size-3.5 text-muted-foreground" />
            )}
            {line.description}
          </Button>
        ) : line.isSubtotal ? (
          // Set like the Total it leads up to, and against the same
          // edge: both are sums of the lines above them, not lines.
          line.description
        ) : (
          // Indented by the chevron a group's label sits after, so
          // every label on a level starts at the same place whether or
          // not its row has one.
          <span className={CHEVRON_GUTTER}>
            <RowLabel description={line.description} />
          </span>
        )}
      </td>
      <td className={`${style.cell} ${COUNT_COLUMN} text-right ${style.text}`}>
        {isAddon
          ? NO_UNITS
          : line.count != null && !line.isSubtotal
            ? formatCount(line.count)
            : ''}
      </td>
      <td
        className={`${style.cell} ${COST_COLUMN_DIVIDER} text-right ${style.text}`}>
        {line.isSubtotal ? '' : formatMinutes(line.quantityMinutes)}
      </td>
      <td
        className={`${style.cell} text-right ${style.text} ${depth === 0 ? 'text-muted-foreground' : ''}`}>
        {line.usageFraction != null
          ? formatPercent(line.usageFraction)
          : depth < 2 && !moneyOnly
            ? formatPrice(line.pricePerMinute)
            : ''}
      </td>
      <td
        className={`${style.cell} text-right ${style.text} ${line.isSubtotal ? 'font-bold' : ''}`}>
        {formatCurrency(line.amountUsd)}
      </td>
    </tr>
  )
}
